import { IndexableType } from 'dexie';
import Dexie from 'dexie';

// 默认操作的表名
const UPLOAD_TABLE_NAME = 'uploadMap';

// 抽象类-IndexDB操作
abstract class DBManager {
  static getDBInstance: (dbName: string) => Promise<Dexie>;
  abstract add: (item: any, tableName: string) => Promise<any>;
  abstract get: (key: string, tableName: string) => Promise<any>;
  abstract update: (
    key: any,
    changes: { [keyPath: string]: any },
    tableName: string,
  ) => Promise<any>;
  abstract delete: (key: string) => Promise<any>;
}

// 操作localStorage
class StorageManage {
  public setStorageItem(key: string, value: any) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(error);
    }
  }
  public getStorageItem(key: string) {
    try {
      const item = localStorage.getItem(key);
      if (item) return JSON.parse(item);
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}

// 暴露出去的Help类，包含IndexDB与LocalStorage的所有基础操作
export class StorageHelper<T extends Dexie> extends StorageManage implements DBManager {
  // Index DB的表名
  static schema = {
    uploadMap: '&workspace, value',
  };
  // Index DB的版本号
  static verno: number = 1;

  private constructor(private DBInstance: T) {
    super();
  }

  // 获取StorageHelper类的实例
  static async getInstance() {
    const DBInstance = await StorageHelper.getDBInstance();
    return new StorageHelper(DBInstance);
  }

  // 获取Index DB的实例
  static async getDBInstance(dbName = 'XYUpload') {
    const dbExists = await Dexie.exists(dbName);
    const db = new Dexie(dbName);
    try {
      if (dbExists) {
        // 如果db关了，先打开db，因为查db的tables需要db在open状态
        if (!db.isOpen()) await db.open();
        // 设置当前db的版本号
        StorageHelper.verno = db.verno;
        // 如果存在数据库，检查表是否存在
        const tables = db.tables.map((table) => table.name);
        // 检查schema里是否有新增的表
        for (const [table] of Object.entries(StorageHelper.schema)) {
          // 如果有新增的表，先关闭db，把当前db的版本+1(不更新版本号，无法更新浏览器IndexDB中新增的表数据)
          if (!tables.includes(table)) {
            if (db.isOpen()) db.close();
            StorageHelper.verno++;
            break;
          }
        }
        // 如果db关了，说明有新增的表需要更新
        if (!db.isOpen()) {
          // 新增数据表
          db.version(StorageHelper.verno).stores(StorageHelper.schema);
          // 重新打开DB
          await db.open();
        }
      } else {
        // 如果不存在数据库，创建数据库&表
        const version = db.version(StorageHelper.verno);
        version.stores(StorageHelper.schema);
        await db.open();
      }
    } catch (error) {
      console.error(error);
    }

    return db;
  }

  // 向Index DB的表中添加数据
  public async add(item: any, tableName: string = UPLOAD_TABLE_NAME) {
    try {
      const table = this.DBInstance.table(tableName);
      return await table.add(item);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // 获取Index DB表中的数据
  public async get(key: string, tableName: string = UPLOAD_TABLE_NAME) {
    try {
      const table = this.DBInstance.table(tableName);
      return await table.get(key);
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  // 更新Index DB表中的数据
  public async update(
    key: string,
    changes: { [keyPath: string]: any },
    tableName: string = UPLOAD_TABLE_NAME,
  ) {
    try {
      const table = this.DBInstance.table(tableName);
      return await table.update(key, changes);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // 删除Index DB表中的数据
  public async delete(key: string, tableName: string = UPLOAD_TABLE_NAME) {
    try {
      const table = this.DBInstance.table(tableName);
      return await table.delete(key);
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
